import React, { useState, useEffect } from 'react';
import { User, AlertCircle, Plus, X, Loader } from 'lucide-react';
import { accounts } from '../../services/apiService';
import { Loader as SectionLoader } from '../UI/LoadingSpinner';
import ModalPortal from '../UI/ModalPortal';
import { Autocomplete } from '@anyemp/ui-kit';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import AccountAccordionItem from '../UI/AccountAccordionItem';

const AccountTab = ({ userId, talentType, accountsList: externalAccountsList, loading: externalLoading, error: externalError, onRefresh }) => {
  const { hasPermission, user } = useAuth();
  const [accountsList, setAccountsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [editingSubAiLimitDate, setEditingSubAiLimitDate] = useState(null);
  const [limitDateEditValue, setLimitDateEditValue] = useState('');
  const [savingSubAiLimitDate, setSavingSubAiLimitDate] = useState(null);
  const [expandedAccountId, setExpandedAccountId] = useState(null);

  // Состояние для назначения аккаунтов
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [loadingAvailableAccounts, setLoadingAvailableAccounts] = useState(false);
  const [attachingAccounts, setAttachingAccounts] = useState(false);

  // Use external data if provided, otherwise fetch internally
  const isExternalData = externalAccountsList !== undefined;
  const displayAccountsList = isExternalData ? externalAccountsList : accountsList;
  const displayLoading = isExternalData ? (externalLoading ?? false) : loading;
  const displayError = isExternalData ? externalError : error;

  useEffect(() => {
    if (isExternalData) {
      // If external data is provided, don't fetch
      setLoading(false);
      return;
    }
    
    if (userId) {
      fetchAccountData();
    } else {
      setLoading(false);
    }
  }, [userId, isExternalData]);

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await accounts.getUserAccount(userId);
      
      if (response.success && response.data) {
        // response.data теперь массив аккаунтов
        setAccountsList(Array.isArray(response.data) ? response.data : [response.data]);
      } else {
        setError(response.error || 'Failed to fetch account data');
      }
    } catch (err) {
      console.error('Error fetching account data:', err);
      setError('Error loading account data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const toLocalDatetimeValue = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const tzOffsetMs = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 16);
  };

  const refetchAccounts = async () => {
    if (isExternalData && onRefresh) {
      await onRefresh();
    } else if (!isExternalData && userId) {
      await fetchAccountData();
    }
  };

  const handleEditLimitDate = (subAi) => {
    setEditingSubAiLimitDate(subAi.id);
    setLimitDateEditValue(toLocalDatetimeValue(subAi.limit_date));
  };

  const handleCancelLimitDate = () => {
    setEditingSubAiLimitDate(null);
    setLimitDateEditValue('');
  };

  const handleSaveLimitDate = async (subAiId) => {
    setSavingSubAiLimitDate(subAiId);
    try {
      const limitDate = limitDateEditValue ? new Date(limitDateEditValue).toISOString() : null;
      const result = await accounts.updateSubAiLimitDate(subAiId, limitDate);
      if (result.success) {
        toast.success('Limit date updated successfully');
        setEditingSubAiLimitDate(null);
        setLimitDateEditValue('');
        await refetchAccounts();
      } else {
        toast.error(result.error || 'Failed to update limit date');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to update limit date');
    } finally {
      setSavingSubAiLimitDate(null);
    }
  };

  const canEditSubAiLimitDate = user?.id && userId && String(user.id) === String(userId);

  // Проверка прав на назначение аккаунтов
  const canAssignAccounts = talentType
    ? hasPermission('talent-service', `${talentType}_assigned_accounts`, 'create')
    : hasPermission('account-service', 'account', 'create');

  // Проверка прав доступа для показа пароля
  const canViewPassword = () => {
    // Проверка прав доступа 'account': ['password-read']
    if (hasPermission('account-service', 'account', 'password-read')) {
      return true;
    }
    // Проверка, является ли авторизированный пользователь равен AusUserId (auth_user_id) в профиле таланта
    if (user?.id && userId && user.id === userId) {
      return true;
    }
    return false;
  };

  const togglePasswordVisibility = (id, type) => {
    const key = `${id}-${type}`;
    setVisiblePasswords(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Загрузка списка всех доступных аккаунтов
  const fetchAvailableAccounts = async () => {
    try {
      setLoadingAvailableAccounts(true);
      const response = await accounts.getAllAccounts({ all: true });
      
      if (response.success && response.data) {
        // Исключаем уже назначенные аккаунты
        const assignedAccountIds = displayAccountsList.map(acc => acc.id);
        const available = Array.isArray(response.data) 
          ? response.data.filter(acc => !assignedAccountIds.includes(acc.id))
          : [];
        setAvailableAccounts(available);
      } else {
        toast.error(response.error || 'Failed to load available accounts');
      }
    } catch (err) {
      console.error('Error fetching available accounts:', err);
      toast.error('Error loading available accounts');
    } finally {
      setLoadingAvailableAccounts(false);
    }
  };

  // Открытие модального окна для назначения аккаунтов
  const handleOpenAssignModal = () => {
    setShowAssignModal(true);
    setSelectedAccountIds([]);
    fetchAvailableAccounts();
  };

  // Закрытие модального окна
  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedAccountIds([]);
    setAvailableAccounts([]);
  };

  // Преобразование аккаунтов в формат для MultiSelect (label включает name и login для поиска)
  const getAccountOptions = () => {
    return availableAccounts.map(account => {
      const primary = account.name || account.login || account.tool?.name || `Account #${account.id}`;
      const toolPart = account.tool?.name ? ` (${account.tool.name})` : '';
      const loginPart = account.login && account.login !== primary ? ` • ${account.login}` : '';
      return {
        value: String(account.id),
        label: `${primary}${toolPart}${loginPart}`
      };
    });
  };

  // Назначение выбранных аккаунтов
  const handleAttachAccounts = async () => {
    if (selectedAccountIds.length === 0) {
      toast.error('Please select at least one account');
      return;
    }

    try {
      setAttachingAccounts(true);
      const response = await accounts.attachAccounts(userId, selectedAccountIds);
      
      if (response.success) {
        toast.success(`Successfully assigned ${selectedAccountIds.length} account(s)`);
        handleCloseAssignModal();
        
        // Обновляем список аккаунтов
        if (!isExternalData) {
          await fetchAccountData();
        } else {
          // Если данные внешние, нужно обновить их в родительском компоненте
          // Для этого можно вызвать callback или просто показать сообщение
          toast.success('Please refresh to see updated accounts');
        }
      } else {
        toast.error(response.error || 'Failed to assign accounts');
      }
    } catch (err) {
      console.error('Error attaching accounts:', err);
      toast.error('Error assigning accounts');
    } finally {
      setAttachingAccounts(false);
    }
  };

  // Проверка прав доступа
  // Разрешаем просмотр собственных аккаунтов без специальных permissions
  const canViewAccounts = hasPermission('account-service', 'account', 'read') || 
                          (user?.id && userId && user.id === userId);
  
  if (!canViewAccounts) {
    return (
      <div className="relative bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 dark:from-red-950/20 dark:via-orange-950/20 dark:to-yellow-950/20 border-2 border-red-200 dark:border-red-800 rounded-2xl p-12 text-center overflow-hidden animate-fade-in">
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl"></div>

        <div className="relative">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-red-500/20 blur-2xl rounded-full"></div>
            <div className="relative p-4 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 rounded-2xl">
              <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">Access Denied</h3>
          <p className="text-red-600/80 dark:text-red-400/80">You don't have permission to view account information</p>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950/20 dark:via-indigo-950/20 dark:to-purple-950/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-12 text-center overflow-hidden animate-fade-in">
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl"></div>

        <div className="relative">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-full"></div>
            <div className="relative p-4 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 rounded-2xl">
              <User className="h-12 w-12 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">User Not Assigned</h3>
          <p className="text-light-text-secondary dark:text-dark-text-secondary">To display account information, a user must be assigned to this talent</p>
        </div>
      </div>
    );
  }

  if (displayLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <SectionLoader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  if (displayError) {
    return (
      <div className="relative bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 dark:from-red-950/20 dark:via-rose-950/20 dark:to-pink-950/20 border-2 border-red-200 dark:border-red-800 rounded-2xl p-12 text-center overflow-hidden animate-fade-in">
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink-500/10 rounded-full blur-3xl"></div>

        <div className="relative">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-red-500/20 blur-2xl rounded-full"></div>
            <div className="relative p-4 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 rounded-2xl">
              <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-3">{displayError}</h3>
          {!isExternalData && (
            <button
              onClick={fetchAccountData}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 dark:from-red-500 dark:to-red-600 dark:hover:from-red-600 dark:hover:to-red-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (displayAccountsList.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary flex items-center">
            <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-3 shadow-md">
              <User className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
              Accounts
            </span>
            <span className="ml-2 px-2.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-sm font-semibold rounded-full">
              0
            </span>
          </h3>
          {userId && !isExternalData && canAssignAccounts && (
            <button
              onClick={handleOpenAssignModal}
              className="group relative inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white text-sm font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-all duration-200"
            >
              <Plus className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
              Assign Accounts
            </button>
          )}
        </div>

        <div className="relative bg-gradient-to-br from-light-bg-primary via-light-bg-tertiary to-light-bg-secondary dark:from-dark-bg-tertiary dark:via-dark-bg-secondary dark:to-dark-bg-tertiary border-2 border-dashed border-light-border-primary dark:border-dark-border-primary rounded-2xl p-12 text-center overflow-hidden">
          {/* Декоративные элементы */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl"></div>

          <div className="relative">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-primary-500/20 blur-2xl rounded-full"></div>
              <div className="relative p-4 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 rounded-2xl">
                <User className="h-12 w-12 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">No accounts found</h3>
            <p className="text-light-text-secondary dark:text-dark-text-secondary mb-6">Account information is not available for this user</p>

            {userId && !isExternalData && canAssignAccounts && (
              <button
                onClick={handleOpenAssignModal}
                className="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <Plus className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform duration-300" />
                Get Started - Assign Accounts
              </button>
            )}
          </div>
        </div>

        {/* Modal для назначения аккаунтов */}
        {showAssignModal && (
          <ModalPortal onClose={attachingAccounts ? undefined : handleCloseAssignModal}>
            <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl min-w-0 flex-col overflow-hidden rounded-2xl border border-light-border-primary bg-light-bg-primary shadow-2xl animate-scale-in dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:max-h-[calc(100dvh-4rem)]">
              {/* Декоративная полоса сверху */}
              <div className="h-1.5 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500"></div>

              {/* Header */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-light-border-primary bg-gradient-to-r p-4 dark:border-dark-border-primary sm:p-6 from-light-bg-primary to-light-bg-secondary dark:from-dark-bg-secondary dark:to-dark-bg-tertiary">
                <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary flex items-center">
                  <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-3 shadow-md">
                    <Plus className="h-5 w-5 text-white" />
                  </div>
                  Assign Accounts
                </h3>
                <button
                  onClick={handleCloseAssignModal}
                  className="p-2 rounded-lg text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-all duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 scrollbar-light">
                {loadingAvailableAccounts ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="relative">
                      <SectionLoader variant="component" size="md" fullHeight={false} />
                    </div>
                    <span className="mt-4 text-light-text-secondary dark:text-dark-text-secondary font-medium">Loading available accounts...</span>
                  </div>
                ) : availableAccounts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="relative inline-block mb-6">
                      <div className="absolute inset-0 bg-light-text-tertiary/10 dark:bg-dark-text-tertiary/10 blur-xl rounded-full"></div>
                      <AlertCircle className="relative mx-auto h-16 w-16 text-light-text-tertiary dark:text-dark-text-tertiary" />
                    </div>
                    <p className="text-lg font-semibold text-light-text-secondary dark:text-dark-text-secondary mb-2">No available accounts</p>
                    <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">All accounts are already assigned to this user</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Autocomplete
                      multiple
                      clearable
                      fullWidth
                      options={getAccountOptions()}
                      value={selectedAccountIds.map(String)}
                      onChange={(vals) => setSelectedAccountIds((vals || []).map(Number))}
                      placeholder="Search and select accounts..."
                      label="Select accounts to assign to this user"
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-light-border-primary bg-light-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-dark-border-primary dark:bg-dark-bg-tertiary sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:p-6">
                <button
                  onClick={handleCloseAssignModal}
                  disabled={attachingAccounts}
                  className="px-5 py-2.5 border-2 border-light-border-primary dark:border-dark-border-primary text-light-text-primary dark:text-dark-text-primary font-semibold rounded-xl hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-secondary hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAttachAccounts}
                  disabled={attachingAccounts || selectedAccountIds.length === 0}
                  className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {attachingAccounts ? (
                    <span className="flex items-center">
                      <Loader className="h-4 w-4 mr-2 inline animate-spin" />
                      Assigning...
                    </span>
                  ) : (
                    `Assign ${selectedAccountIds.length > 0 ? `(${selectedAccountIds.length})` : ''}`
                  )}
                </button>
              </div>
            </div>
          </ModalPortal>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary flex items-center">
            <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-3 shadow-md">
              <User className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
              Accounts
            </span>
            <span className="ml-2 px-2.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-semibold rounded-full">
              {displayAccountsList.length}
            </span>
          </h3>
          {userId && !isExternalData && canAssignAccounts && (
            <button
              onClick={handleOpenAssignModal}
              className="group relative inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white text-sm font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-all duration-200"
            >
              <Plus className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
              Assign Accounts
            </button>
          )}
        </div>

        {/* Список аккаунтов — Accordion */}
        <div className="space-y-3">
          {displayAccountsList.map((account, index) => {
            const accountKey = account.id || index;
            const isExpanded = expandedAccountId === accountKey;
            const accountData = {
              ...account,
              two_factor_enabled: undefined,
              is_hidden_from_employee: undefined,
              userAssignments: undefined,
              socialAccounts: undefined,
              created_at: undefined,
              updated_at: undefined,
            };

            return (
              <AccountAccordionItem
                key={accountKey}
                account={accountData}
                accountKey={accountKey}
                isExpanded={isExpanded}
                onToggle={(key) => setExpandedAccountId(isExpanded ? null : key)}
                index={index}
                formatDate={formatDate}
                canViewPassword={canViewPassword()}
                visiblePasswords={visiblePasswords}
                onTogglePassword={togglePasswordVisibility}
                canEditSubAiLimitDate={canEditSubAiLimitDate}
                editingSubAiLimitDate={editingSubAiLimitDate}
                limitDateEditValue={limitDateEditValue}
                onChangeLimitDate={setLimitDateEditValue}
                onEditLimitDate={handleEditLimitDate}
                onSaveLimitDate={handleSaveLimitDate}
                onCancelLimitDate={handleCancelLimitDate}
                savingSubAiLimitDate={savingSubAiLimitDate}
                badges={
                  <>
                    {account.tool?.name && (
                      <span className="hidden sm:inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-primary text-light-text-secondary dark:text-dark-text-secondary border border-light-border-primary/50 dark:border-dark-border-primary/50">
                        {account.tool.name}
                      </span>
                    )}
                    {Array.isArray(account.subAi) && account.subAi.length > 0 && (
                      <span className="hidden sm:inline-flex px-2 py-1 text-xs font-semibold rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                        {account.subAi.length} SubAi
                      </span>
                    )}
                  </>
                }
              />
            );
          })}
        </div>
      </div>

      {/* Modal для назначения аккаунтов */}
      {showAssignModal && (
        <ModalPortal onClose={attachingAccounts ? undefined : handleCloseAssignModal}>
          <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl min-w-0 flex-col overflow-hidden rounded-2xl border border-light-border-primary bg-light-bg-primary shadow-2xl animate-scale-in dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:max-h-[calc(100dvh-4rem)]">
            {/* Декоративная полоса сверху */}
            <div className="h-1.5 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500"></div>

            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-light-border-primary bg-gradient-to-r p-4 dark:border-dark-border-primary sm:p-6 from-light-bg-primary to-light-bg-secondary dark:from-dark-bg-secondary dark:to-dark-bg-tertiary">
              <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary flex items-center">
                <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-3 shadow-md">
                  <Plus className="h-5 w-5 text-white" />
                </div>
                Assign Accounts
              </h3>
              <button
                onClick={handleCloseAssignModal}
                className="p-2 rounded-lg text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-all duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-light">
              {loadingAvailableAccounts ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <SectionLoader variant="component" size="md" fullHeight={false} />
                  </div>
                  <span className="mt-4 text-light-text-secondary dark:text-dark-text-secondary font-medium">Loading available accounts...</span>
                </div>
              ) : availableAccounts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-light-text-tertiary/10 dark:bg-dark-text-tertiary/10 blur-xl rounded-full"></div>
                    <AlertCircle className="relative mx-auto h-16 w-16 text-light-text-tertiary dark:text-dark-text-tertiary" />
                  </div>
                  <p className="text-lg font-semibold text-light-text-secondary dark:text-dark-text-secondary mb-2">No available accounts</p>
                  <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">All accounts are already assigned to this user</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Autocomplete
                    multiple
                    clearable
                    fullWidth
                    options={getAccountOptions()}
                    value={selectedAccountIds.map(String)}
                    onChange={(vals) => setSelectedAccountIds((vals || []).map(Number))}
                    placeholder="Search and select accounts..."
                    label="Select accounts to assign to this user"
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-light-border-primary bg-light-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-dark-border-primary dark:bg-dark-bg-tertiary sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:p-6">
              <button
                onClick={handleCloseAssignModal}
                disabled={attachingAccounts}
                className="px-5 py-2.5 border-2 border-light-border-primary dark:border-dark-border-primary text-light-text-primary dark:text-dark-text-primary font-semibold rounded-xl hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-secondary hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleAttachAccounts}
                disabled={attachingAccounts || selectedAccountIds.length === 0}
                className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {attachingAccounts ? (
                  <span className="flex items-center">
                    <Loader className="h-4 w-4 mr-2 inline animate-spin" />
                    Assigning...
                  </span>
                ) : (
                  `Assign ${selectedAccountIds.length > 0 ? `(${selectedAccountIds.length})` : ''}`
                )}
              </button>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default AccountTab;

